import React, { useState, useCallback } from 'react';
import { SketchPicker, ColorResult } from 'react-color';
import { Input } from './input';
import { Label } from './label';
import { Button } from './button';
import { Pipette, Palette } from 'lucide-react';
import { cn } from '../../lib/utils';
import { usePalettes } from '@/hooks/usePalettes';

interface AdvancedColorPickerProps {
  color: string;
  onChange: (newColor: string) => void;
  className?: string;
}

// Type declaration for EyeDropper API
declare global {
  interface Window {
    EyeDropper?: {
      new(): {
        open(): Promise<{ sRGBHex: string }>;
      };
    };
  }
}

const AdvancedColorPicker: React.FC<AdvancedColorPickerProps> = ({
  color,
  onChange,
  className,
}) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [isPickingColor, setIsPickingColor] = useState(false);

  const { allPalettes, isLoading: palettesLoading } = usePalettes();

  const handleColorChange = useCallback((colorResult: ColorResult) => {
    onChange(colorResult.hex);
  }, [onChange]);

  const handleClick = () => {
    setDisplayColorPicker(!displayColorPicker);
  };

  const handleClose = () => {
    setDisplayColorPicker(false);
  };

  const handleColorSelectFromPalette = (newColor: string) => {
    onChange(newColor);
    // setDisplayColorPicker(false); // Optionally close picker
  };

  const handleEyeDropper = useCallback(async () => {
    if (!window.EyeDropper) {
      alert('Eyedropper is not supported in this browser. Please use Chrome/Edge 95+ or Safari 16.4+');
      return;
    }

    try {
      setIsPickingColor(true);
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      onChange(result.sRGBHex);
      setDisplayColorPicker(false); // Close picker after selecting color
    } catch (error) {
      // User cancelled or error occurred
      console.log('Eyedropper cancelled or failed:', error);
    } finally {
      setIsPickingColor(false);
    }
  }, [onChange]);

  const isEyeDropperSupported = typeof window !== 'undefined' && 'EyeDropper' in window;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Color preview, hex input, and eyedropper button */}
      <div className="flex items-center gap-3">
        <div 
          className="w-12 h-12 rounded-md border-2 border-muted shadow-inner cursor-pointer hover:shadow-md transition-shadow"
          style={{ backgroundColor: color }}
          onClick={handleClick}
          title="Click to open color picker"
        />
        <div className="flex-1">
          <Label htmlFor={`hex-input-${color}`} className="text-sm font-medium">HEX Color</Label>
          <div className="flex gap-2">
            <Input
              id={`hex-input-${color}`}
              value={color.toUpperCase()}
              onChange={(e) => {
                const newColor = e.target.value;
                if (/^#[0-9A-Fa-f]{0,6}$/.test(newColor)) {
                  onChange(newColor);
                }
              }}
              className="font-mono text-sm"
              placeholder="#FF0000"
            />
            {isEyeDropperSupported && (
              <Button
                variant="outline"
                size="icon"
                onClick={handleEyeDropper}
                disabled={isPickingColor}
                className="flex-shrink-0"
                title="Pick color from screen"
              >
                {isPickingColor ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                ) : (
                  <Pipette className="h-4 w-4" />
                )}
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={handleClick}
              className="flex-shrink-0"
              title="Open color picker"
            >
              <Palette className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Color picker popup */}
      {displayColorPicker && (
        <div className="relative">
          <div 
            className="fixed inset-0 z-[100]" 
            onClick={handleClose}
          />
          <div 
            className="absolute top-0 left-0 z-[101] bg-white rounded-lg shadow-xl border"
          >
            <SketchPicker
              color={color}
              onChange={handleColorChange}
              onChangeComplete={handleColorChange}
              disableAlpha={false}
              presetColors={[
                '#FF6900', '#FCB900', '#7BDCB5', '#00D084',
                '#8ED1FC', '#0693E3', '#ABB8C3', '#EB144C',
                '#F78DA7', '#9900EF', '#FF4500', '#FF6B35',
                '#F7931E', '#FFD23F', '#C4E538', '#7CB342',
                '#2196F3', '#9C27B0', '#673AB7', '#3F51B5',
                '#00BCD4', '#009688', '#4CAF50', '#8BC34A'
              ]}
              width="280px"
            />
            {/* Palette Selection Section */}
            <div className="p-3 border-t border-gray-200">
              <h4 className="font-medium text-sm mb-2 text-gray-700">Choose from Palette</h4>
              {palettesLoading ? (
                <div className="text-xs text-gray-500">Loading palettes...</div>
              ) : allPalettes.length === 0 ? (
                <div className="text-xs text-gray-500">
                  No palettes available. Manage palettes in settings.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto pr-1">
                  {allPalettes.map((palette) => (
                    <Button
                      key={palette.id}
                      variant="outline"
                      size="sm"
                      onClick={() => handleColorSelectFromPalette(palette.computedDefaultColor)}
                      className="flex items-center justify-start gap-2 w-full text-xs"
                      title={`${palette.name} - ${palette.computedDefaultColor}`}
                    >
                      <div 
                        className="w-4 h-4 rounded-sm border border-gray-300 flex-shrink-0"
                        style={{ backgroundColor: palette.computedDefaultColor }}
                      />
                      <span className="truncate">{palette.name}</span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedColorPicker; 